import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';
import type {
  League,
  LeagueSquad,
  SquadPlayer,
  Player,
  Transfer,
  Match,
  PlayerPool,
  PlayerPoolEntry,
  PlayerPoolSnapshot,
  LeaderboardSnapshot,
  StandingEntry,
  User,
  TennisMatch,
  TennisMatchPerformance,
  TennisTournament,
} from '../types/database';
import { calculateSquadPoints, DEFAULT_TENNIS_SQUAD_RULES, DEFAULT_TENNIS_SCORING_CONFIG } from '../utils/pointsCalculation';
import { recomputeEntryPoints, dedupeNewPerformances, buildPerformanceFromTennisMatch, buildApiKeyByName, lookupApiKey, buildOpponentSeedLookup, recomputeSquadFromPool } from '../utils/tennisMatchPerformance';

// Collection References
const COLLECTIONS = {
  USERS: 'users',
  LEAGUES: 'leagues',
  SQUADS: 'squads',
  PLAYERS: 'players',
  PLAYER_POOLS: 'playerPools',
  PLAYER_POOL_SNAPSHOTS: 'playerPoolSnapshots',
  TRANSFERS: 'transfers',
  MATCHES: 'matches',
  PLAYER_PERFORMANCES: 'playerPerformances',
  POINTS_CONFIGS: 'pointsConfigs',
  LEADERBOARD_SNAPSHOTS: 'leaderboardSnapshots',
  // Tennis-specific collections (separate from cricket for clean data organisation)
  TENNIS_SQUADS: 'tennisSquads',
  TENNIS_PLAYER_POOLS: 'tennisPlayerPools',
} as const;

// Tennis subcollection paths under the /tennis/data/ container document.
// Keeping all tennis collections grouped here gives a single visible "tennis"
// node in the Firebase console (vs. four scattered root-level collections).
export const TENNIS_PATHS = {
  ROOT: 'tennis',
  ROOT_DOC: 'data',
  TOURNAMENTS: 'tournaments',
  PLAYERS: 'players',
  MATCHES: 'matches',
  SYNC_RUNS: 'syncRuns',
} as const;

const tennisCol = (sub: 'tournaments' | 'players' | 'matches' | 'syncRuns') =>
  collection(db, TENNIS_PATHS.ROOT, TENNIS_PATHS.ROOT_DOC, sub);

const tennisDoc = (sub: 'tournaments' | 'players' | 'matches' | 'syncRuns', docId: string) =>
  doc(db, TENNIS_PATHS.ROOT, TENNIS_PATHS.ROOT_DOC, sub, docId);

// Utility function to convert Firestore Timestamp to Date
const convertTimestamps = (data: any): any => {
  if (data === null || typeof data !== 'object') return data;

  // Handle arrays separately to preserve array structure
  if (Array.isArray(data)) {
    return data.map(item => convertTimestamps(item));
  }

  const converted = { ...data };
  Object.keys(converted).forEach(key => {
    if (converted[key] instanceof Timestamp) {
      converted[key] = converted[key].toDate();
    } else if (typeof converted[key] === 'object') {
      converted[key] = convertTimestamps(converted[key]);
    }
  });

  return converted;
};

function stripUndefined<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined)
  ) as Partial<T>;
}

/**
 * Recursively strip `undefined` field values from a value before sending to
 * Firestore. `updateDoc` rejects any payload where some path contains
 * `undefined` — even deep inside nested array elements. We preserve Date and
 * Timestamp instances unchanged (they're not plain objects).
 */
function deepStripUndefined<T>(value: T): T {
  if (value === undefined) return undefined as unknown as T;
  if (value === null) return value;
  if (Array.isArray(value)) {
    return (value
      .map((v) => deepStripUndefined(v))
      .filter((v) => v !== undefined) as unknown) as T;
  }
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value;
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const cleaned = deepStripUndefined(v);
      if (cleaned !== undefined) out[k] = cleaned;
    }
    return out as T;
  }
  return value;
}

// League Operations
export const leagueService = {
  // Create a new league
  async create(league: Omit<League, 'id' | 'createdAt'>): Promise<string> {
    const docRef = await addDoc(collection(db, COLLECTIONS.LEAGUES), stripUndefined({
      ...league,
      createdAt: new Date(),
    }));
    return docRef.id;
  },

  // Get league by ID
  async getById(leagueId: string): Promise<League | null> {
    const docRef = doc(db, COLLECTIONS.LEAGUES, leagueId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return convertTimestamps({ id: docSnap.id, ...docSnap.data() }) as League;
    }
    return null;
  },

  // Get leagues for a user
  async getForUser(userId: string): Promise<League[]> {
    const q = query(
      collection(db, COLLECTIONS.LEAGUES),
      where('participants', 'array-contains', userId)
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc =>
      convertTimestamps({ id: doc.id, ...doc.data() }) as League
    );
  },

  // Get all leagues (admin only)
  async getAll(): Promise<League[]> {
    const q = query(collection(db, COLLECTIONS.LEAGUES));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc =>
      convertTimestamps({ id: doc.id, ...doc.data() }) as League
    );
  },

  // Update league
  async update(leagueId: string, updates: Partial<League>): Promise<void> {
    const docRef = doc(db, COLLECTIONS.LEAGUES, leagueId);
    await updateDoc(docRef, stripUndefined(updates));
  },

  // Get league by code
  async getByCode(code: string): Promise<League | null> {
    const q = query(
      collection(db, COLLECTIONS.LEAGUES),
      where('code', '==', code.toUpperCase())
    );
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return convertTimestamps({ id: doc.id, ...doc.data() }) as League;
    }
    return null;
  },

  // Join league
  async joinLeague(leagueId: string, userId: string): Promise<void> {
    const docRef = doc(db, COLLECTIONS.LEAGUES, leagueId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const league = docSnap.data() as League;
      
      // Check if user is already a participant
      if (league.participants.includes(userId)) {
        throw new Error('You are already a member of this league');
      }
      
      // Check if league is full
      if (league.participants.length >= league.maxParticipants) {
        throw new Error('This league is full');
      }
      
      const updatedParticipants = [...league.participants, userId];
      
      await updateDoc(docRef, {
        participants: updatedParticipants,
      });
    } else {
      throw new Error('League not found');
    }
  },

  // Join league by code
  async joinLeagueByCode(code: string, userId: string): Promise<League> {
    const league = await this.getByCode(code);
    
    if (!league) {
      throw new Error('No leagues found for this code');
    }
    
    await this.joinLeague(league.id, userId);
    return league;
  },

  // Delete league (admin only)
  async delete(leagueId: string): Promise<void> {
    const docRef = doc(db, COLLECTIONS.LEAGUES, leagueId);
    await deleteDoc(docRef);
  },

  /**
   * Remove selected participants (contestants) from a league.
   * Also removes their squads from that league.
   */
  async removeParticipantsFromLeague(
    leagueId: string,
    userIds: string[]
  ): Promise<{
    removedParticipants: number;
    deletedSquads: number;
  }> {
    if (!leagueId) {
      throw new Error('League ID is required');
    }

    const uniqueUserIds = Array.from(new Set(userIds.filter(Boolean)));
    if (uniqueUserIds.length === 0) {
      return { removedParticipants: 0, deletedSquads: 0 };
    }

    const league = await this.getById(leagueId);
    if (!league) {
      throw new Error('League not found');
    }

    const userIdSet = new Set(uniqueUserIds);
    const updatedParticipants = (league.participants || []).filter((id) => !userIdSet.has(id));
    const updatedSquadsSubmitted = (league.squadsSubmitted || []).filter((id) => !userIdSet.has(id));
    const removedParticipants = (league.participants || []).length - updatedParticipants.length;

    await updateDoc(doc(db, COLLECTIONS.LEAGUES, leagueId), {
      participants: updatedParticipants,
      squadsSubmitted: updatedSquadsSubmitted,
    });

    const squads = await squadService.getByLeague(leagueId);
    const squadsToDelete = squads.filter((squad) => userIdSet.has(squad.userId));
    let deletedSquads = 0;

    if (squadsToDelete.length > 0) {
      const batch = writeBatch(db);
      squadsToDelete.forEach((squad) => {
        batch.delete(doc(db, COLLECTIONS.SQUADS, squad.id));
      });
      await batch.commit();
      deletedSquads = squadsToDelete.length;
    }

    await leaderboardSnapshotService.create(leagueId);

    return { removedParticipants, deletedSquads };
  },

  // Get leagues by player pool ID (admin only)
  async getByPlayerPool(playerPoolId: string): Promise<League[]> {
    const q = query(
      collection(db, COLLECTIONS.LEAGUES),
      where('playerPoolId', '==', playerPoolId)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc =>
      convertTimestamps({ id: doc.id, ...doc.data() }) as League
    );
  },

  // Real-time listener for league updates
  subscribeToLeague(leagueId: string, callback: (league: League | null) => void): () => void {
    const docRef = doc(db, COLLECTIONS.LEAGUES, leagueId);

    return onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        callback(convertTimestamps({ id: doc.id, ...doc.data() }) as League);
      } else {
        callback(null);
      }
    });
  },
};

// Squad Operations
export const squadService = {
  // Create squad for user in league
  async create(squad: Omit<LeagueSquad, 'id' | 'createdAt'>): Promise<string> {
    const docRef = await addDoc(collection(db, COLLECTIONS.SQUADS), {
      ...squad,
      createdAt: new Date(),
    });
    return docRef.id;
  },

  // Get squad by ID
  async getById(squadId: string): Promise<LeagueSquad | null> {
    const docRef = doc(db, COLLECTIONS.SQUADS, squadId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return convertTimestamps({ id: docSnap.id, ...docSnap.data() }) as LeagueSquad;
    }
    return null;
  },

  // Get squad by user and league
  async getByUserAndLeague(userId: string, leagueId: string): Promise<LeagueSquad | null> {
    const q = query(
      collection(db, COLLECTIONS.SQUADS),
      where('userId', '==', userId),
      where('leagueId', '==', leagueId)
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return convertTimestamps({ id: doc.id, ...doc.data() }) as LeagueSquad;
    }
    return null;
  },

  // Get all squads in a league
  async getByLeague(leagueId: string): Promise<LeagueSquad[]> {
    const q = query(
      collection(db, COLLECTIONS.SQUADS),
      where('leagueId', '==', leagueId),
      orderBy('totalPoints', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => 
      convertTimestamps({ id: doc.id, ...doc.data() }) as LeagueSquad
    );
  },

  // Update squad
  async update(squadId: string, updates: Partial<LeagueSquad>): Promise<void> {
    const docRef = doc(db, COLLECTIONS.SQUADS, squadId);
    await updateDoc(docRef, updates);
  },

  // Submit squad (lock for tournament)
  async submit(squadId: string): Promise<void> {
    const docRef = doc(db, COLLECTIONS.SQUADS, squadId);
    await updateDoc(docRef, {
      isSubmitted: true,
      submittedAt: new Date(),
    });
  },

  /**
   * Copy a squad to a target league, resetting all scoring fields to zero.
   * If the user already has a squad in the target league, it is overwritten.
   * Returns the id of the created/updated squad.
   */
  async copyToLeague(sourceSquadId: string, targetLeagueId: string): Promise<string> {
    const source = await squadService.getById(sourceSquadId);
    if (!source) throw new Error(`Source squad ${sourceSquadId} not found`);

    const resetFields = {
      leagueId: targetLeagueId,
      totalPoints: 0,
      captainPoints: 0,
      viceCaptainPoints: 0,
      xFactorPoints: 0,
      predictionBonusPoints: 0,
      rank: 0,
      previousRank: 0,
      rankChange: 0,
      pointsGainedToday: 0,
      matchPoints: {},
      transfersUsed: 0,
      benchTransfersUsed: 0,
      flexibleTransfersUsed: 0,
      midSeasonTransfersUsed: 0,
      transferHistory: [],
      bankedPoints: 0,
      powerplayPoints: 0,
      powerplayCompleted: false,
      hiddenPlayerPoints: 0,
      isSubmitted: true,
    };

    const preserved = {
      userId: source.userId,
      squadName: source.squadName,
      players: source.players,
      captainId: source.captainId,
      viceCaptainId: source.viceCaptainId,
      xFactorId: source.xFactorId,
      hiddenPlayerId: source.hiddenPlayerId,
      hiddenPlayerName: source.hiddenPlayerName,
      hiddenPlayerTeam: source.hiddenPlayerTeam,
      hiddenPlayerRole: source.hiddenPlayerRole,
      predictions: source.predictions,
      powerplayMatchNumber: source.powerplayMatchNumber,
      isValid: source.isValid,
      validationErrors: source.validationErrors,
    };

    const payload = stripUndefined({ ...preserved, ...resetFields });

    const existing = await squadService.getByUserAndLeague(source.userId, targetLeagueId);
    if (existing) {
      await squadService.update(existing.id, payload);
      return existing.id;
    } else {
      return await squadService.create(payload as unknown as Omit<LeagueSquad, 'id' | 'createdAt'>);
    }
  },

  /**
   * Permanently delete a squad document.
   */
  async deleteSquad(squadId: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTIONS.SQUADS, squadId));
  },
};

// Tennis Squad Operations (tennisSquads collection)
export const tennisSquadService = {
  async create(squad: Omit<LeagueSquad, 'id' | 'createdAt'>): Promise<string> {
    const docRef = await addDoc(collection(db, COLLECTIONS.TENNIS_SQUADS), {
      ...squad,
      createdAt: new Date(),
    });
    return docRef.id;
  },

  async getById(squadId: string): Promise<LeagueSquad | null> {
    const docSnap = await getDoc(doc(db, COLLECTIONS.TENNIS_SQUADS, squadId));
    if (docSnap.exists()) {
      return convertTimestamps({ id: docSnap.id, ...docSnap.data() }) as LeagueSquad;
    }
    return null;
  },

  async getByUserAndLeague(userId: string, leagueId: string): Promise<LeagueSquad | null> {
    const q = query(
      collection(db, COLLECTIONS.TENNIS_SQUADS),
      where('userId', '==', userId),
      where('leagueId', '==', leagueId)
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      const d = snap.docs[0];
      return convertTimestamps({ id: d.id, ...d.data() }) as LeagueSquad;
    }
    return null;
  },

  async getByLeague(leagueId: string): Promise<LeagueSquad[]> {
    const q = query(
      collection(db, COLLECTIONS.TENNIS_SQUADS),
      where('leagueId', '==', leagueId)
    );
    const snap = await getDocs(q);
    const squads = snap.docs.map(d => convertTimestamps({ id: d.id, ...d.data() }) as LeagueSquad);
    return squads.sort((a, b) => (b.totalPoints || 0) - (a.totalPoints || 0));
  },

  async update(squadId: string, updates: Partial<LeagueSquad>): Promise<void> {
    await updateDoc(doc(db, COLLECTIONS.TENNIS_SQUADS, squadId), updates);
  },

  async deleteSquad(squadId: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTIONS.TENNIS_SQUADS, squadId));
  },
};

// Player Operations
export const playerService = {
  // Get all active players
  async getActive(): Promise<Player[]> {
    const q = query(
      collection(db, COLLECTIONS.PLAYERS),
      where('isActive', '==', true),
      orderBy('name')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => 
      convertTimestamps({ id: doc.id, ...doc.data() }) as Player
    );
  },

  // Get players by team
  async getByTeam(team: string): Promise<Player[]> {
    const q = query(
      collection(db, COLLECTIONS.PLAYERS),
      where('team', '==', team),
      where('isActive', '==', true)
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => 
      convertTimestamps({ id: doc.id, ...doc.data() }) as Player
    );
  },

  // Get player by ID
  async getById(playerId: string): Promise<Player | null> {
    const docRef = doc(db, COLLECTIONS.PLAYERS, playerId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return convertTimestamps({ id: docSnap.id, ...docSnap.data() }) as Player;
    }
    return null;
  },

  // Add new player (admin only)
  async create(player: Omit<Player, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const docRef = await addDoc(collection(db, COLLECTIONS.PLAYERS), {
      ...player,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    return docRef.id;
  },

  // Update player
  async update(playerId: string, updates: Partial<Player>): Promise<void> {
    const docRef = doc(db, COLLECTIONS.PLAYERS, playerId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: new Date(),
    });
  },
};

// Transfer Operations
export const transferService = {
  // Create transfer request
  async create(transfer: Omit<Transfer, 'id' | 'requestedAt'>): Promise<string> {
    const docRef = await addDoc(collection(db, COLLECTIONS.TRANSFERS), {
      ...transfer,
      requestedAt: new Date(),
    });
    return docRef.id;
  },

  // Get transfers for a league
  async getByLeague(leagueId: string): Promise<Transfer[]> {
    const q = query(
      collection(db, COLLECTIONS.TRANSFERS),
      where('leagueId', '==', leagueId)
      // Note: Removed orderBy to avoid needing a composite index
      // Sorting is done in-memory instead
    );
    const querySnapshot = await getDocs(q);

    const transfers = querySnapshot.docs.map(doc =>
      convertTimestamps({ id: doc.id, ...doc.data() }) as Transfer
    );

    // Sort in memory by requestedAt descending (newest first)
    return transfers.sort((a, b) =>
      new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime()
    );
  },

  // Get pending transfers for admin approval
  async getPending(leagueId: string): Promise<Transfer[]> {
    const q = query(
      collection(db, COLLECTIONS.TRANSFERS),
      where('leagueId', '==', leagueId),
      where('status', '==', 'pending'),
      orderBy('requestedAt', 'asc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => 
      convertTimestamps({ id: doc.id, ...doc.data() }) as Transfer
    );
  },

  // Approve/Reject transfer
  async updateStatus(
    transferId: string, 
    status: 'approved' | 'rejected', 
    adminId: string,
    rejectionReason?: string
  ): Promise<void> {
    const docRef = doc(db, COLLECTIONS.TRANSFERS, transferId);
    await updateDoc(docRef, {
      status,
      processedAt: new Date(),
      processedBy: adminId,
      ...(rejectionReason && { rejectionReason }),
    });
  },
};

// Match Operations
export const matchService = {
  // Get upcoming matches
  async getUpcoming(): Promise<Match[]> {
    const q = query(
      collection(db, COLLECTIONS.MATCHES),
      where('status', '==', 'upcoming'),
      orderBy('startTime', 'asc'),
      limit(10)
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => 
      convertTimestamps({ id: doc.id, ...doc.data() }) as Match
    );
  },

  // Get matches for a league
  async getByLeague(leagueId: string): Promise<Match[]> {
    const q = query(
      collection(db, COLLECTIONS.MATCHES),
      where('leagueIds', 'array-contains', leagueId),
      orderBy('startTime', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => 
      convertTimestamps({ id: doc.id, ...doc.data() }) as Match
    );
  },
};

// Player Pool Operations
export const playerPoolService = {
  // Create a new player pool
  async create(pool: Omit<PlayerPool, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const docRef = await addDoc(collection(db, COLLECTIONS.PLAYER_POOLS), {
      ...pool,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    return docRef.id;
  },

  // Get all active player pools
  async getAll(): Promise<PlayerPool[]> {
    const q = query(
      collection(db, COLLECTIONS.PLAYER_POOLS),
      where('isActive', '==', true)
    );
    const querySnapshot = await getDocs(q);

    // Sort by createdAt in memory to avoid needing a composite index
    const pools = querySnapshot.docs.map(doc =>
      convertTimestamps({ id: doc.id, ...doc.data() }) as PlayerPool
    );

    return pools.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  },

  // Get player pool by ID
  async getById(poolId: string): Promise<PlayerPool | null> {
    const docRef = doc(db, COLLECTIONS.PLAYER_POOLS, poolId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return convertTimestamps({ id: docSnap.id, ...docSnap.data() }) as PlayerPool;
    }
    return null;
  },

  // Get player pools created by a user
  async getByCreator(userId: string): Promise<PlayerPool[]> {
    const q = query(
      collection(db, COLLECTIONS.PLAYER_POOLS),
      where('creatorId', '==', userId)
    );
    const querySnapshot = await getDocs(q);

    // Sort by createdAt in memory to avoid needing a composite index
    const pools = querySnapshot.docs.map(doc =>
      convertTimestamps({ id: doc.id, ...doc.data() }) as PlayerPool
    );

    return pools.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  },

  // Update player pool
  async update(poolId: string, updates: Partial<PlayerPool>): Promise<void> {
    const docRef = doc(db, COLLECTIONS.PLAYER_POOLS, poolId);

    // Ensure players is always an array if it's being updated
    const updateData: any = {
      ...updates,
      updatedAt: new Date(),
    };

    if (updates.players !== undefined) {
      // Force players to be an array
      updateData.players = Array.isArray(updates.players)
        ? updates.players
        : (updates.players ? Object.values(updates.players) : []);
    }

    await updateDoc(docRef, updateData);
  },

  // Add player to pool
  async addPlayer(poolId: string, player: PlayerPoolEntry): Promise<void> {
    const poolDoc = await this.getById(poolId);
    if (!poolDoc) {
      throw new Error('Player pool not found');
    }

    const updatedPlayers = [...poolDoc.players, player];
    await this.update(poolId, { players: updatedPlayers });
  },

  // Update player in pool
  async updatePlayer(poolId: string, playerId: string, updates: Partial<PlayerPoolEntry>): Promise<void> {
    const poolDoc = await this.getById(poolId);
    if (!poolDoc) {
      throw new Error('Player pool not found');
    }

    const updatedPlayers = poolDoc.players.map(p =>
      p.playerId === playerId ? { ...p, ...updates, lastUpdated: new Date() } : p
    );
    await this.update(poolId, { players: updatedPlayers });

    // DISABLED: Automatic recalculation can corrupt points due to bad pointsWhenRoleAssigned data
    // Admin must manually recalculate after fixing role timestamps
    // if (updates.points !== undefined) {
    //   await this.recalculateLeaguesUsingPool(poolId);
    // }
    if (updates.points !== undefined) {
      console.warn('⚠️ AUTOMATIC RECALCULATION DISABLED - Manually recalculate from Admin Panel after fixing role timestamps');
    }
  },

  // Remove player from pool
  async removePlayer(poolId: string, playerId: string): Promise<void> {
    const poolDoc = await this.getById(poolId);
    if (!poolDoc) {
      throw new Error('Player pool not found');
    }

    const updatedPlayers = poolDoc.players.filter(p => p.playerId !== playerId);
    await this.update(poolId, { players: updatedPlayers });
  },

  // Update multiple player points at once
  async updatePlayerPoints(
    poolId: string,
    pointsUpdates: { playerId: string; points: number }[],
    updateMessage?: string,
    updatedBy?: string
  ): Promise<void> {
    const poolDoc = await this.getById(poolId);
    if (!poolDoc) {
      throw new Error('Player pool not found');
    }

    const updatedPlayers = poolDoc.players.map(player => {
      const update = pointsUpdates.find(u => u.playerId === player.playerId);
      if (update) {
        return {
          ...player,
          points: update.points,
          lastUpdated: new Date(),
        };
      }
      return player;
    });

    // Update the player pool with new points and optional update message
    const updateData: any = { players: updatedPlayers };
    if (updateMessage) {
      updateData.lastUpdateMessage = updateMessage;
    }

    await this.update(poolId, updateData);

    // Create a snapshot of the player pool with point changes
    try {
      await playerPoolSnapshotService.create(poolId, updateMessage, updatedBy);
      console.log('✅ Player pool snapshot created successfully');
    } catch (error) {
      console.error('Error creating player pool snapshot:', error);
      // Don't fail the update if snapshot creation fails
    }

    // DISABLED: Automatic recalculation can corrupt points due to bad pointsWhenRoleAssigned data
    // Admin must manually recalculate after fixing role timestamps
    // await this.recalculateLeaguesUsingPool(poolId);
    console.warn('⚠️ AUTOMATIC RECALCULATION DISABLED - Manually recalculate from Admin Panel after fixing role timestamps');
  },

  // Find all leagues using a specific player pool
  async getLeaguesUsingPool(poolId: string): Promise<League[]> {
    const q = query(
      collection(db, COLLECTIONS.LEAGUES),
      where('playerPoolId', '==', poolId)
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc =>
      convertTimestamps({ id: doc.id, ...doc.data() }) as League
    );
  },

  // Recalculate squad points and create snapshots for all leagues using this pool
  async recalculateLeaguesUsingPool(poolId: string): Promise<void> {
    console.log(`Recalculating leagues using player pool: ${poolId}`);

    // Get the updated player pool
    const playerPool = await this.getById(poolId);
    if (!playerPool) {
      throw new Error('Player pool not found');
    }

    // Find all leagues using this pool
    const leagues = await this.getLeaguesUsingPool(poolId);
    console.log(`Found ${leagues.length} leagues using this pool`);

    if (leagues.length === 0) {
      return;
    }

    // For each league, recalculate squad points using batch writes
    const updatePromises = leagues.map(async (league) => {
      try {
        console.log(`Updating squads for league ${league.id}`);

        // Get all squads in this league
        const squads = await squadService.getByLeague(league.id);
        console.log(`Found ${squads.length} squads to update`);

        if (squads.length === 0) {
          console.log(`No squads in league ${league.id}, skipping`);
          return;
        }

        // Use batch writes for efficiency
        // IMPORTANT: Always update ALL squads on every player pool save to ensure consistency
        const batch = writeBatch(db);

        for (const squad of squads) {
          // Update player points in the squad to match current pool
          const updatedPlayers = squad.players.map(squadPlayer => {
            const poolPlayer = playerPool.players.find(p => p.playerId === squadPlayer.playerId);

            if (poolPlayer) {
              return {
                ...squadPlayer,
                points: poolPlayer.points,
              };
            }
            return squadPlayer;
          });

          // FIXED: Use shared calculation utility for consistency
          // This ensures pool updates use the SAME formula as transfers
          const calculatedPoints = calculateSquadPoints(
            updatedPlayers,
            league.squadSize,
            squad.captainId,
            squad.viceCaptainId,
            squad.xFactorId,
            squad.bankedPoints || 0
          );

          const { totalPoints, captainPoints, viceCaptainPoints, xFactorPoints } = calculatedPoints;

          // Always update every squad on each player pool save
          const squadRef = doc(db, COLLECTIONS.SQUADS, squad.id);
          batch.update(squadRef, {
            players: updatedPlayers,
            totalPoints,
            captainPoints,
            viceCaptainPoints,
            xFactorPoints,
            lastUpdated: new Date(),
          });
        }

        // Commit all squad updates
        await batch.commit();
        console.log(`Updated ${squads.length} squads in league ${league.id}`);

        // Create a new leaderboard snapshot for this league
        console.log(`Creating snapshot for league: ${league.id}`);
        await leaderboardSnapshotService.create(league.id);
        console.log(`Leaderboard snapshot created for league ${league.id}`);
      } catch (error) {
        console.error(`Error recalculating league ${league.id}:`, error);
      }
    });

    await Promise.all(updatePromises);
    console.log('Recalculation complete for all leagues');
  },

  // Delete player pool (soft delete by setting isActive to false)
  async delete(poolId: string): Promise<void> {
    await this.update(poolId, { isActive: false });
  },

  // Real-time listener for player pool updates
  subscribeToPool(poolId: string, callback: (pool: PlayerPool | null) => void): () => void {
    const docRef = doc(db, COLLECTIONS.PLAYER_POOLS, poolId);

    return onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        callback(convertTimestamps({ id: doc.id, ...doc.data() }) as PlayerPool);
      } else {
        callback(null);
      }
    });
  },
};

// Tennis Player Pool Operations (tennisPlayerPools collection).
//
// Storage shape (post-migration):
//   tennisPlayerPools/{poolId}                          — pool doc (no perfs inline)
//   tennisPlayerPools/{poolId}/playerPerformances/{pid} — per-player perf records
//
// Performances live in a subcollection because the inline array on the parent
// doc exceeded Firestore's 1 MB doc limit at scale (each match carries the
// full `allStats` capture for display). The PUBLIC API of this service is
// unchanged: `getById` still returns a pool where each player has
// `tennisMatchPerformances` populated — UI callers don't need to change.
const TENNIS_PERFS_SUBCOLL = 'playerPerformances';

const _perfsCollectionRef = (poolId: string) =>
  collection(db, COLLECTIONS.TENNIS_PLAYER_POOLS, poolId, TENNIS_PERFS_SUBCOLL);

const _perfsDocRef = (poolId: string, playerId: string) =>
  doc(db, COLLECTIONS.TENNIS_PLAYER_POOLS, poolId, TENNIS_PERFS_SUBCOLL, playerId);

const _stripPerfsFromPlayers = (players: PlayerPoolEntry[]): PlayerPoolEntry[] =>
  (players || []).map((p) => {
    const { tennisMatchPerformances, ...rest } = p as any;
    return rest as PlayerPoolEntry;
  });

async function _readAllPerformances(
  poolId: string
): Promise<Map<string, TennisMatchPerformance[]>> {
  const snap = await getDocs(_perfsCollectionRef(poolId));
  const map = new Map<string, TennisMatchPerformance[]>();
  for (const d of snap.docs) {
    const data = convertTimestamps(d.data() || {});
    const perfs = Array.isArray(data.performances) ? data.performances : [];
    map.set(d.id, perfs as TennisMatchPerformance[]);
  }
  return map;
}

async function _writePerformancesDoc(
  poolId: string,
  playerId: string,
  performances: TennisMatchPerformance[],
  updatedBy?: string
): Promise<void> {
  await setDoc(
    _perfsDocRef(poolId, playerId),
    deepStripUndefined({
      playerId,
      performances,
      lastUpdated: new Date(),
      ...(updatedBy ? { updatedBy } : {}),
    })
  );
}

export const tennisPlayerPoolService = {
  async create(pool: Omit<PlayerPool, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const docRef = await addDoc(collection(db, COLLECTIONS.TENNIS_PLAYER_POOLS), {
      ...pool,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    return docRef.id;
  },

  async getAll(): Promise<PlayerPool[]> {
    const q = query(collection(db, COLLECTIONS.TENNIS_PLAYER_POOLS), where('isActive', '==', true));
    const snap = await getDocs(q);
    const pools = snap.docs.map(d => convertTimestamps({ id: d.id, ...d.data() }) as PlayerPool);
    return pools.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  },

  /**
   * Returns the pool with each player's `tennisMatchPerformances` hydrated
   * from the subcollection. Read sites — UI match-history view, scoring
   * recompute, import dedupe — keep working without code changes.
   */
  async getById(poolId: string): Promise<PlayerPool | null> {
    const docSnap = await getDoc(doc(db, COLLECTIONS.TENNIS_PLAYER_POOLS, poolId));
    if (!docSnap.exists()) return null;
    const pool = convertTimestamps({ id: docSnap.id, ...docSnap.data() }) as PlayerPool;
    const perfsByPlayer = await _readAllPerformances(poolId);
    pool.players = (pool.players || []).map((p) => ({
      ...p,
      tennisMatchPerformances: perfsByPlayer.get(p.playerId) ?? [],
    }));
    return pool;
  },

  async getByCreator(userId: string): Promise<PlayerPool[]> {
    const q = query(collection(db, COLLECTIONS.TENNIS_PLAYER_POOLS), where('creatorId', '==', userId));
    const snap = await getDocs(q);
    const pools = snap.docs.map(d => convertTimestamps({ id: d.id, ...d.data() }) as PlayerPool);
    return pools.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  },

  async update(poolId: string, updates: Partial<PlayerPool>): Promise<void> {
    const updateData: any = { ...updates, updatedAt: new Date() };
    if (updates.players !== undefined) {
      const arr = Array.isArray(updates.players)
        ? updates.players
        : (updates.players ? Object.values(updates.players as any) : []);
      // Never persist tennisMatchPerformances inline — that's the subcollection's job.
      updateData.players = _stripPerfsFromPlayers(arr as PlayerPoolEntry[]);
    }
    // Firestore `updateDoc` rejects any path that contains `undefined`, even
    // deep inside nested array elements (e.g. a TennisMatchPerformance with
    // `round: undefined` would blow up the entire write). Deep-strip first.
    await updateDoc(
      doc(db, COLLECTIONS.TENNIS_PLAYER_POOLS, poolId),
      deepStripUndefined(updateData)
    );
  },

  async addPlayer(poolId: string, player: PlayerPoolEntry): Promise<void> {
    const pool = await this.getById(poolId);
    if (!pool) throw new Error('Tennis player pool not found');
    await this.update(poolId, { players: [...pool.players, player] });
  },

  async updatePlayer(poolId: string, playerId: string, updates: Partial<PlayerPoolEntry>): Promise<void> {
    const pool = await this.getById(poolId);
    if (!pool) throw new Error('Tennis player pool not found');
    const updatedPlayers = pool.players.map(p =>
      p.playerId === playerId ? { ...p, ...updates, lastUpdated: new Date() } : p
    );
    await this.update(poolId, { players: updatedPlayers });
  },

  async removePlayer(poolId: string, playerId: string): Promise<void> {
    const pool = await this.getById(poolId);
    if (!pool) throw new Error('Tennis player pool not found');
    await this.update(poolId, { players: pool.players.filter(p => p.playerId !== playerId) });
  },

  async updatePlayerPoints(
    poolId: string,
    pointsUpdates: { playerId: string; points: number }[],
    updateMessage?: string,
    updatedBy?: string
  ): Promise<void> {
    const pool = await this.getById(poolId);
    if (!pool) throw new Error('Tennis player pool not found');
    const updatedPlayers = pool.players.map(p => {
      const hit = pointsUpdates.find(u => u.playerId === p.playerId);
      return hit ? { ...p, points: hit.points, lastUpdated: new Date() } : p;
    });
    const updateData: any = { players: updatedPlayers };
    if (updateMessage) updateData.lastUpdateMessage = updateMessage;
    await this.update(poolId, updateData);
    try {
      await playerPoolSnapshotService.create(poolId, updateMessage, updatedBy);
    } catch (e) {
      console.error('Error creating tennis pool snapshot:', e);
    }
    // Cascade recalculation to all leagues that use this pool
    await this.cascadeUpdateToLeagues(poolId, pointsUpdates);
  },

  // Append a single per-match performance to a player and recompute their total.
  // Writes the merged perf array to the subcollection doc; the parent pool
  // doc only carries the new running `points` total (plus the usual metadata).
  async addMatchPerformance(
    poolId: string,
    playerId: string,
    perf: TennisMatchPerformance,
    updateMessage?: string,
    updatedBy?: string
  ): Promise<void> {
    const pool = await this.getById(poolId);
    if (!pool) throw new Error('Tennis player pool not found');

    let newPoints = 0;
    const updatedPlayers = pool.players.map(p => {
      if (p.playerId !== playerId) return p;
      const updated: PlayerPoolEntry = {
        ...p,
        tennisMatchPerformances: [...(p.tennisMatchPerformances || []), perf],
        lastUpdated: new Date(),
        updatedBy,
      };
      newPoints = recomputeEntryPoints(updated);
      updated.points = newPoints;
      return updated;
    });

    const targetPlayer = updatedPlayers.find(p => p.playerId === playerId);
    if (targetPlayer) {
      await _writePerformancesDoc(
        poolId,
        playerId,
        targetPlayer.tennisMatchPerformances ?? [],
        updatedBy
      );
    }

    await this.update(poolId, {
      players: updatedPlayers,
      ...(updateMessage ? { lastUpdateMessage: updateMessage } : {}),
    });
    try {
      await playerPoolSnapshotService.create(poolId, updateMessage, updatedBy);
    } catch (e) {
      console.error('Error creating tennis pool snapshot:', e);
    }
    await this.cascadeUpdateToLeagues(poolId, [{ playerId, points: newPoints }]);
  },

  // Remove a per-match performance from a player and recompute their total.
  async deleteMatchPerformance(
    poolId: string,
    playerId: string,
    perfId: string,
    updatedBy?: string
  ): Promise<void> {
    const pool = await this.getById(poolId);
    if (!pool) throw new Error('Tennis player pool not found');

    let newPoints = 0;
    const updatedPlayers = pool.players.map(p => {
      if (p.playerId !== playerId) return p;
      const updated: PlayerPoolEntry = {
        ...p,
        tennisMatchPerformances: (p.tennisMatchPerformances || []).filter(m => m.id !== perfId),
        lastUpdated: new Date(),
        updatedBy,
      };
      newPoints = recomputeEntryPoints(updated);
      updated.points = newPoints;
      return updated;
    });

    const targetPlayer = updatedPlayers.find(p => p.playerId === playerId);
    if (targetPlayer) {
      await _writePerformancesDoc(
        poolId,
        playerId,
        targetPlayer.tennisMatchPerformances ?? [],
        updatedBy
      );
    }

    await this.update(poolId, { players: updatedPlayers });
    await this.cascadeUpdateToLeagues(poolId, [{ playerId, points: newPoints }]);
  },

  // Bulk import: append deduped performances to many players. Each affected
  // player gets a single subcollection write with their full merged perf
  // array; the parent doc is updated once with the new running totals.
  async importMatchPerformances(
    poolId: string,
    perfsByPlayerId: {
      playerId: string;
      // Optional — populated by `fetchImportCandidates` when the entry was
      // resolved via a name match. We write it back so future imports
      // can skip the name step and match by key.
      resolvedApiPlayerKey?: number;
      performances: TennisMatchPerformance[];
    }[],
    updateMessage?: string,
    updatedBy?: string
  ): Promise<void> {
    const pool = await this.getById(poolId);
    if (!pool) throw new Error('Tennis player pool not found');

    const byId = new Map(perfsByPlayerId.map(e => [e.playerId, e]));
    const pointsUpdates: { playerId: string; points: number }[] = [];
    const perfsToWrite: { playerId: string; performances: TennisMatchPerformance[] }[] = [];

    const updatedPlayers = pool.players.map(p => {
      const incoming = byId.get(p.playerId);
      if (!incoming || incoming.performances.length === 0) return p;
      const existing = p.tennisMatchPerformances || [];
      const toAdd = dedupeNewPerformances(existing, incoming.performances);
      // Even when no NEW performances were dedup-filtered through, we still
      // want to persist the resolved apiPlayerKey if we have one.
      const keyToWrite =
        typeof p.apiPlayerKey === 'number' ? undefined : incoming.resolvedApiPlayerKey;
      if (toAdd.length === 0 && keyToWrite === undefined) return p;
      const merged = [...existing, ...toAdd];
      const updated: PlayerPoolEntry = {
        ...p,
        tennisMatchPerformances: merged,
        ...(keyToWrite !== undefined ? { apiPlayerKey: keyToWrite } : {}),
        lastUpdated: new Date(),
        updatedBy,
      };
      updated.points = recomputeEntryPoints(updated);
      pointsUpdates.push({ playerId: p.playerId, points: updated.points });
      if (toAdd.length > 0) {
        perfsToWrite.push({ playerId: p.playerId, performances: merged });
      }
      return updated;
    });

    if (pointsUpdates.length === 0) return;

    // Write each affected player's perfs to the subcollection. One doc per
    // player keeps individual writes well under the 1 MB cap (~8 KB per
    // match × 7 rounds max ≈ 56 KB).
    for (const { playerId, performances } of perfsToWrite) {
      await _writePerformancesDoc(poolId, playerId, performances, updatedBy);
    }

    await this.update(poolId, {
      players: updatedPlayers,
      ...(updateMessage ? { lastUpdateMessage: updateMessage } : {}),
    });
    try {
      await playerPoolSnapshotService.create(poolId, updateMessage, updatedBy);
    } catch (e) {
      console.error('Error creating tennis pool snapshot:', e);
    }
    await this.cascadeUpdateToLeagues(poolId, pointsUpdates);
  },

  // Resolve importable match performances for a pool by reading the tournament
  // keys off the tennis leagues that use this pool, fetching their synced
  // matches, and building deduped candidate records per player (via apiPlayerKey).
  async fetchImportCandidates(
    poolId: string
  ): Promise<{
    tournamentFound: boolean;
    candidates: {
      playerId: string;
      // When a player was matched by name (rather than via a pre-set
      // apiPlayerKey), expose the resolved key so importMatchPerformances
      // can auto-populate the pool entry for next time.
      resolvedApiPlayerKey?: number;
      performances: TennisMatchPerformance[];
    }[];
    matchedPlayerCount: number;   // # of pool entries we could resolve to an API key
    unmatchedPlayerCount: number; // # of pool entries with no name OR apiPlayerKey match
  }> {
    const pool = await this.getById(poolId);
    if (!pool) throw new Error('Tennis player pool not found');

    const leaguesSnap = await getDocs(
      query(
        collection(db, COLLECTIONS.LEAGUES),
        where('playerPoolId', '==', poolId),
        where('sport', '==', 'tennis')
      )
    );
    const keys = new Set<number>();
    leaguesSnap.docs.forEach(d => {
      const l = d.data() as any;
      if (typeof l.tennisMensTournamentKey === 'number') keys.add(l.tennisMensTournamentKey);
      if (typeof l.tennisWomensTournamentKey === 'number') keys.add(l.tennisWomensTournamentKey);
    });
    if (keys.size === 0) return { tournamentFound: false, candidates: [], matchedPlayerCount: 0, unmatchedPlayerCount: 0 };

    const matchArrays = await Promise.all(Array.from(keys).map(k => tennisMatchService.getByTournament(k)));
    const matches = matchArrays.flat();

    // Build the (apiCanonicalKey → playerKey) index from /tennis/data/players —
    // that subcollection is populated by /api/tennis-sync (the same flow as
    // the DO TENNIS API CALL admin button), so it's the canonical source of
    // truth for api-tennis player keys. Fall back to scanning match data for
    // any players that somehow aren't in that subcollection yet.
    const playersSnap = await getDocs(collection(db, TENNIS_PATHS.ROOT, TENNIS_PATHS.ROOT_DOC, TENNIS_PATHS.PLAYERS));
    const playerEntries: { playerKey: number; name: string }[] = playersSnap.docs.map((d) => {
      const data = d.data() as any;
      return { playerKey: Number(data.playerKey ?? d.id), name: String(data.name ?? '') };
    });
    // Add match-derived names as a safety net.
    for (const m of matches) {
      if (typeof m.firstPlayerKey === 'number' && m.firstPlayerName) {
        playerEntries.push({ playerKey: m.firstPlayerKey, name: m.firstPlayerName });
      }
      if (typeof m.secondPlayerKey === 'number' && m.secondPlayerName) {
        playerEntries.push({ playerKey: m.secondPlayerKey, name: m.secondPlayerName });
      }
    }
    const apiKeyByName = buildApiKeyByName(playerEntries);

    // Use the canonical name matcher for opponent seed resolution — pool
    // entries can be in "Lastname Firstname" / "Firstname Lastname" / api
    // short form, while match opponents are always in api short form. A naive
    // normalize-and-compare misses every name → no upset bonus ever fires.
    const lookup = buildOpponentSeedLookup(pool.players);
    const config = pool.tennisConfig ?? DEFAULT_TENNIS_SCORING_CONFIG;

    const candidates: { playerId: string; resolvedApiPlayerKey?: number; performances: TennisMatchPerformance[] }[] = [];
    let matchedPlayerCount = 0;
    let unmatchedPlayerCount = 0;

    for (const p of pool.players) {
      // Resolve apiPlayerKey: prefer the explicit one, fall back to a name match.
      let apiKey: number | undefined =
        typeof p.apiPlayerKey === 'number' ? p.apiPlayerKey : undefined;
      let resolvedFromName = false;
      if (apiKey === undefined) {
        const looked = lookupApiKey(apiKeyByName, p.name);
        if (typeof looked === 'number') {
          apiKey = looked;
          resolvedFromName = true;
        }
      }
      if (apiKey === undefined) {
        unmatchedPlayerCount++;
        continue;
      }
      matchedPlayerCount++;

      const perfs: TennisMatchPerformance[] = [];
      for (const m of matches) {
        const perf = buildPerformanceFromTennisMatch(m, apiKey, p.seed, config, lookup, p.gender);
        if (perf) perfs.push(perf);
      }
      const fresh = dedupeNewPerformances(p.tennisMatchPerformances || [], perfs);
      if (fresh.length > 0) {
        candidates.push({
          playerId: p.playerId,
          ...(resolvedFromName ? { resolvedApiPlayerKey: apiKey } : {}),
          performances: fresh,
        });
      }
    }
    return { tournamentFound: true, candidates, matchedPlayerCount, unmatchedPlayerCount };
  },

  async cascadeUpdateToLeagues(
    poolId: string,
    playerUpdates: { playerId: string; points: number }[]
  ): Promise<void> {
    // Fetch the pool's current players once so every league's recompute can
    // self-heal squad references whose stored playerId no longer exists in
    // the pool (the Zuber/Matteo failure mode).
    let poolPlayers: { playerId: string; name: string; points: number }[] | undefined;
    try {
      const poolSnap = await getDoc(doc(db, COLLECTIONS.TENNIS_PLAYER_POOLS, poolId));
      if (poolSnap.exists()) {
        poolPlayers = ((poolSnap.data().players ?? []) as any[]).map((p) => ({
          playerId: p.playerId, name: p.name, points: p.points ?? 0,
        }));
      }
    } catch (e) {
      console.warn(`Could not load pool ${poolId} for self-healing lookups; proceeding with sparse updates only.`, e);
    }

    const leaguesQuery = query(
      collection(db, COLLECTIONS.LEAGUES),
      where('playerPoolId', '==', poolId),
      where('sport', '==', 'tennis')
    );
    const leaguesSnap = await getDocs(leaguesQuery);
    const leagues = leaguesSnap.docs.map(d => ({ id: d.id, ...d.data() }) as any);
    for (const league of leagues) {
      try {
        await this.updateLeagueSquadsAndLeaderboard(league.id, playerUpdates, league, poolPlayers);
      } catch (e) {
        console.error(`Error updating tennis league ${league.id}:`, e);
      }
    }
  },

  async updateLeagueSquadsAndLeaderboard(
    leagueId: string,
    playerUpdates: { playerId: string; points: number }[],
    leagueData?: any,
    poolPlayers?: { playerId: string; name: string; points: number }[]
  ): Promise<void> {
    const league = leagueData ?? await leagueService.getById(leagueId);
    if (!league) return;

    const rules = { ...DEFAULT_TENNIS_SQUAD_RULES, ...(league.tennisSquadRules ?? {}) };
    const config = league.tennisConfig ?? DEFAULT_TENNIS_SCORING_CONFIG;

    const squads = await tennisSquadService.getByLeague(leagueId);
    if (squads.length === 0) return;

    const batch = writeBatch(db);
    for (const squad of squads) {
      const result = recomputeSquadFromPool({
        squad,
        playerUpdates,
        poolPlayers,
        rules,
        config,
      });

      if (result.idPatches.length > 0) {
        console.warn(`[recompute] Auto-healed squad ${squad.id} playerId references:`, result.idPatches);
      }
      if (result.warnings.length > 0) {
        console.warn(`[recompute] Squad ${squad.id} has unresolved players:`, result.warnings);
      }

      batch.update(doc(db, COLLECTIONS.TENNIS_SQUADS, squad.id), {
        players: result.updatedPlayers,
        totalPoints: result.totalPoints,
        predictionBonusPoints: result.predictionBonusPoints,
        unresolvedPlayerWarnings: result.warnings,
        lastUpdated: new Date(),
      });
    }
    await batch.commit();

    try {
      await leaderboardSnapshotService.create(leagueId);
    } catch (e) {
      console.error(`Error creating leaderboard snapshot for league ${leagueId}:`, e);
    }
  },

  async delete(poolId: string): Promise<void> {
    await this.update(poolId, { isActive: false });
  },

  subscribeToPool(poolId: string, callback: (pool: PlayerPool | null) => void): () => void {
    return onSnapshot(doc(db, COLLECTIONS.TENNIS_PLAYER_POOLS, poolId), (d) => {
      callback(d.exists() ? (convertTimestamps({ id: d.id, ...d.data() }) as PlayerPool) : null);
    });
  },
};

// Sport-aware service helpers — pick the right collection based on league sport
export const squadServiceForSport = (sport?: string) =>
  sport === 'tennis' ? tennisSquadService : squadService;

export const playerPoolServiceForSport = (sport?: string) =>
  sport === 'tennis' ? tennisPlayerPoolService : playerPoolService;

// Player Pool Snapshot Operations
export const playerPoolSnapshotService = {
  // Create a new snapshot with deltas from previous snapshot
  async create(poolId: string, updateMessage?: string, updatedBy?: string): Promise<string> {
    try {
      console.log(`Creating player pool snapshot for pool: ${poolId}`);

      // Get the current player pool
      const playerPool = await playerPoolService.getById(poolId);
      if (!playerPool) {
        throw new Error('Player pool not found');
      }

      // Get the previous snapshot to calculate deltas
      const previousSnapshot = await this.getLatest(poolId);

      // Create the snapshot data
      const snapshotData: Omit<PlayerPoolSnapshot, 'id'> = {
        playerPoolId: poolId,
        snapshotDate: new Date(),
        updateMessage: updateMessage || playerPool.lastUpdateMessage,
        updatedBy,
        players: playerPool.players.map(p => ({
          playerId: p.playerId,
          name: p.name,
          team: p.team,
          role: p.role,
          points: p.points,
        })),
        changes: undefined,
      };

      // Calculate deltas if there's a previous snapshot
      if (previousSnapshot) {
        const changes: Array<{
          playerId: string;
          name: string;
          previousPoints: number;
          newPoints: number;
          delta: number;
        }> = [];

        playerPool.players.forEach(currentPlayer => {
          const previousPlayer = previousSnapshot.players.find(p => p.playerId === currentPlayer.playerId);

          if (previousPlayer) {
            const delta = currentPlayer.points - previousPlayer.points;

            // Only include players whose points changed
            if (delta !== 0) {
              changes.push({
                playerId: currentPlayer.playerId,
                name: currentPlayer.name,
                previousPoints: previousPlayer.points,
                newPoints: currentPlayer.points,
                delta,
              });
            }
          } else {
            // New player added to the pool
            changes.push({
              playerId: currentPlayer.playerId,
              name: currentPlayer.name,
              previousPoints: 0,
              newPoints: currentPlayer.points,
              delta: currentPlayer.points,
            });
          }
        });

        // Sort changes by delta (highest first)
        changes.sort((a, b) => b.delta - a.delta);
        snapshotData.changes = changes;
      }
      // No else branch: first snapshot has no prior state, so changes stays undefined

      // Save the snapshot
      const docRef = await addDoc(collection(db, COLLECTIONS.PLAYER_POOL_SNAPSHOTS), {
        ...snapshotData,
        createdAt: new Date(),
      });

      console.log(`Player pool snapshot created with ID: ${docRef.id}`);
      return docRef.id;
    } catch (error) {
      console.error('Error creating player pool snapshot:', error);
      throw error;
    }
  },

  // Get all snapshots for a player pool
  async getByPoolId(poolId: string): Promise<PlayerPoolSnapshot[]> {
    const q = query(
      collection(db, COLLECTIONS.PLAYER_POOL_SNAPSHOTS),
      where('playerPoolId', '==', poolId),
      orderBy('snapshotDate', 'desc')
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc =>
      convertTimestamps({ id: doc.id, ...doc.data() }) as PlayerPoolSnapshot
    );
  },

  // Get the latest snapshot for a player pool
  async getLatest(poolId: string): Promise<PlayerPoolSnapshot | null> {
    const q = query(
      collection(db, COLLECTIONS.PLAYER_POOL_SNAPSHOTS),
      where('playerPoolId', '==', poolId),
      orderBy('snapshotDate', 'desc'),
      limit(1)
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    return convertTimestamps({
      id: querySnapshot.docs[0].id,
      ...querySnapshot.docs[0].data(),
    }) as PlayerPoolSnapshot;
  },

  // Get a specific snapshot by ID
  async getById(snapshotId: string): Promise<PlayerPoolSnapshot | null> {
    const docRef = doc(db, COLLECTIONS.PLAYER_POOL_SNAPSHOTS, snapshotId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return convertTimestamps({ id: docSnap.id, ...docSnap.data() }) as PlayerPoolSnapshot;
    }
    return null;
  },

  // Delete a snapshot
  async delete(snapshotId: string): Promise<void> {
    const docRef = doc(db, COLLECTIONS.PLAYER_POOL_SNAPSHOTS, snapshotId);
    await deleteDoc(docRef);
  },
};

// Leaderboard Snapshot Operations
export const leaderboardSnapshotService = {
  // Create a new leaderboard snapshot
  async create(leagueId: string): Promise<string> {
    try {
      console.log(`Creating leaderboard snapshot for league: ${leagueId}`);

      // Get league data to find player pool
      const league = await leagueService.getById(leagueId);

      // Get player pool version if league uses a player pool
      let playerPoolVersion: string | undefined;
      let pointsUpdatedAt: Date | undefined;
      if (league?.playerPoolId) {
        const poolSvc = league?.sport === 'tennis' ? tennisPlayerPoolService : playerPoolService;
        const playerPool = await poolSvc.getById(league.playerPoolId);
        if (playerPool) {
          playerPoolVersion = playerPool.lastUpdateMessage;
          pointsUpdatedAt = playerPool.updatedAt;
        }
      }

      // Get all squads for this league, filtering to only submitted ones
      const squadSvc = league?.sport === 'tennis' ? tennisSquadService : squadService;
      const allSquads = await squadSvc.getByLeague(leagueId);
      const squads = allSquads.filter(s => s.isSubmitted);
      console.log(`Found ${squads.length} submitted squads in league (${allSquads.length} total)`);

      if (squads.length === 0) {
        console.warn(`No squads found for league ${leagueId}. Creating empty snapshot.`);
      }

      // Get previous snapshot to calculate changes
      const previousSnapshot = await this.getLatest(leagueId);
      const previousStandings = previousSnapshot?.standings || [];
      console.log(`Previous snapshot had ${previousStandings.length} standings`);

      // Get user details for all squads
      const userPromises = squads.map(async (squad) => {
        try {
          const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, squad.userId));
          return userDoc.exists() ? convertTimestamps({ uid: userDoc.id, ...userDoc.data() }) as User : null;
        } catch (error) {
          console.error(`Error fetching user ${squad.userId}:`, error);
          return null;
        }
      });
      const users = await Promise.all(userPromises);
      console.log(`Fetched ${users.filter(u => u !== null).length} user documents`);

    // Build standings with rank changes and points gained
    // First, map squads to standings with calculated totalPoints (including predictionBonusPoints and powerplayPoints)
    const unsortedStandings: StandingEntry[] = squads.map((squad, index) => {
      const user = users[index];
      const previousEntry = previousStandings.find(s => s.userId === squad.userId);
      const previousRank = previousEntry?.rank;
      const previousPoints = previousEntry?.totalPoints || 0;
      const currentTotalPoints = (squad.totalPoints || 0) + (squad.predictionBonusPoints || 0) + (squad.powerplayPoints || 0) + (squad.hiddenPlayerPoints || 0);
      const pointsGainedToday = currentTotalPoints - previousPoints;

      // Get captain, vice-captain, and X-factor details
      const captain = squad.players.find(p => p.playerId === squad.captainId);
      const viceCaptain = squad.players.find(p => p.playerId === squad.viceCaptainId);
      const xFactor = squad.players.find(p => p.playerId === squad.xFactorId);

      // Build standing entry, only including defined fields (Firestore doesn't allow undefined)
      // IMPORTANT: Include predictionBonusPoints and powerplayPoints in totalPoints calculation
      const standing: any = {
        userId: squad.userId,
        squadId: squad.id,
        squadName: squad.squadName,
        displayName: user?.displayName || 'Unknown',
        totalPoints: currentTotalPoints,
        captainPoints: squad.captainPoints || 0,
        viceCaptainPoints: squad.viceCaptainPoints || 0,
        xFactorPoints: squad.xFactorPoints || 0,
        rank: 0, // Will be set after sorting
        rankChange: 0, // Will be calculated after sorting
        pointsGainedToday,
      };

      // Only add optional fields if they're defined
      if (user?.profilePicUrl) standing.profilePicUrl = user.profilePicUrl;
      if (previousRank !== undefined) standing.previousRank = previousRank;
      if (squad.captainId) standing.captainId = squad.captainId;
      if (captain?.playerName) standing.captainName = captain.playerName;
      if (squad.viceCaptainId) standing.viceCaptainId = squad.viceCaptainId;
      if (viceCaptain?.playerName) standing.viceCaptainName = viceCaptain.playerName;
      if (squad.xFactorId) standing.xFactorId = squad.xFactorId;
      if (xFactor?.playerName) standing.xFactorName = xFactor.playerName;
      if (squad.powerplayPoints !== undefined) standing.powerplayPoints = squad.powerplayPoints;
      if (squad.powerplayCompleted !== undefined) standing.powerplayCompleted = squad.powerplayCompleted;

      // Add transfer data
      if (squad.benchTransfersUsed !== undefined) standing.benchTransfersUsed = squad.benchTransfersUsed;
      if (squad.flexibleTransfersUsed !== undefined) standing.flexibleTransfersUsed = squad.flexibleTransfersUsed;
      if (squad.midSeasonTransfersUsed !== undefined) standing.midSeasonTransfersUsed = squad.midSeasonTransfersUsed;
      if (squad.wildcardTransfersUsed !== undefined) standing.wildcardTransfersUsed = squad.wildcardTransfersUsed;
      if (squad.transfersUsed !== undefined) standing.transfersUsed = squad.transfersUsed;

      // Tennis M1/W1 slot scores
      if (league?.sport === 'tennis') {
        const rules = { ...DEFAULT_TENNIS_SQUAD_RULES, ...(league.tennisSquadRules ?? {}) };
        const m1 = squad.players.find(p => (p.gender === 'men') && (p.slotIndex ?? 0) === 0);
        const w1 = squad.players.find(p => (p.gender === 'women') && (p.slotIndex ?? 0) === 0);
        if (m1) standing.m1Points = (m1.points - (m1.pointsAtJoining ?? 0)) * (rules.menMultipliers[0] ?? 2);
        if (w1) standing.w1Points = (w1.points - (w1.pointsAtJoining ?? 0)) * (rules.womenMultipliers[0] ?? 2);
      }

      return standing as StandingEntry;
    });

    // Sort by totalPoints (descending) to get correct rankings
    const standings = unsortedStandings.sort((a, b) => b.totalPoints - a.totalPoints);

    // Now assign ranks and calculate rank changes and lead from next
    standings.forEach((standing, index) => {
      standing.rank = index + 1;
      standing.rankChange = standing.previousRank ? standing.previousRank - standing.rank : 0;

      // Calculate lead from next player
      if (index < standings.length - 1) {
        standing.leadFromNext = standing.totalPoints - standings[index + 1].totalPoints;
      }
    });

    // Find best performer (most points gained)
    let bestPerformer = undefined;
    if (standings.length > 0) {
      const topGainer = standings.reduce((max, current) =>
        current.pointsGainedToday > max.pointsGainedToday ? current : max
      );
      if (topGainer.pointsGainedToday > 0) {
        bestPerformer = {
          userId: topGainer.userId,
          squadName: topGainer.squadName,
          displayName: topGainer.displayName,
          pointsGained: topGainer.pointsGainedToday,
        } as any;
        // Only add profilePicUrl if defined
        if (topGainer.profilePicUrl) {
          bestPerformer.profilePicUrl = topGainer.profilePicUrl;
        }
      }
    }

    // Find rapid riser (most ranks gained)
    let rapidRiser = undefined;
    if (standings.length > 0) {
      const topClimber = standings
        .filter(s => s.rankChange && s.rankChange > 0)
        .reduce((max, current) =>
          (current.rankChange || 0) > (max.rankChange || 0) ? current : max
        , { rankChange: 0 } as StandingEntry);

      if (topClimber.rankChange && topClimber.rankChange > 0) {
        rapidRiser = {
          userId: topClimber.userId,
          squadName: topClimber.squadName,
          displayName: topClimber.displayName,
          ranksGained: topClimber.rankChange,
          previousRank: topClimber.previousRank || 0,
          currentRank: topClimber.rank,
        } as any;
        // Only add profilePicUrl if defined
        if (topClimber.profilePicUrl) {
          rapidRiser.profilePicUrl = topClimber.profilePicUrl;
        }
      }
    }

    // Create snapshot document
    // Only include bestPerformer and rapidRiser if they exist (Firestore doesn't allow undefined)
    const snapshot: Omit<LeaderboardSnapshot, 'id'> = {
      leagueId,
      snapshotDate: new Date(),
      standings,
      createdAt: new Date(),
      ...(bestPerformer && { bestPerformer }),
      ...(rapidRiser && { rapidRiser }),
      ...(playerPoolVersion && { playerPoolVersion }),
      ...(pointsUpdatedAt && { pointsUpdatedAt }),
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.LEADERBOARD_SNAPSHOTS), snapshot);

    // Update squad documents with rank changes
    const squadCollection = league?.sport === 'tennis' ? COLLECTIONS.TENNIS_SQUADS : COLLECTIONS.SQUADS;
    const batch = writeBatch(db);
    standings.forEach((standing) => {
      const squadRef = doc(db, squadCollection, standing.squadId);
      batch.update(squadRef, {
        rank: standing.rank,
        previousRank: standing.previousRank || standing.rank,
        rankChange: standing.rankChange || 0,
        pointsGainedToday: standing.pointsGainedToday,
        lastUpdated: new Date(),
      });
    });
    await batch.commit();

    console.log(`Successfully created leaderboard snapshot ${docRef.id} for league ${leagueId}`);
    return docRef.id;
    } catch (error) {
      console.error(`Error creating leaderboard snapshot for league ${leagueId}:`, error);
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }
      throw error;
    }
  },

  // Get latest snapshot for a league
  async getLatest(leagueId: string): Promise<LeaderboardSnapshot | null> {
    try {
      console.log(`Fetching latest snapshot for league: ${leagueId}`);
      const q = query(
        collection(db, COLLECTIONS.LEADERBOARD_SNAPSHOTS),
        where('leagueId', '==', leagueId),
        orderBy('snapshotDate', 'desc'),
        limit(1)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        console.log(`Found snapshot ${doc.id} for league ${leagueId}`);
        return convertTimestamps({ id: doc.id, ...doc.data() }) as LeaderboardSnapshot;
      }
      console.log(`No snapshots found for league ${leagueId}`);
      return null;
    } catch (error) {
      console.error(`Error fetching latest snapshot for league ${leagueId}:`, error);
      if (error instanceof Error && error.message.includes('index')) {
        console.error('This error may be due to missing Firestore indexes. Check the Firebase console for index creation links.');
      }
      throw error;
    }
  },

  // Get all snapshots for a league
  async getByLeague(leagueId: string): Promise<LeaderboardSnapshot[]> {
    const q = query(
      collection(db, COLLECTIONS.LEADERBOARD_SNAPSHOTS),
      where('leagueId', '==', leagueId),
      orderBy('snapshotDate', 'desc')
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc =>
      convertTimestamps({ id: doc.id, ...doc.data() }) as LeaderboardSnapshot
    );
  },

  // Get recent snapshots for a league (for streak calculation)
  async getRecent(leagueId: string, limitCount: number = 15): Promise<LeaderboardSnapshot[]> {
    const q = query(
      collection(db, COLLECTIONS.LEADERBOARD_SNAPSHOTS),
      where('leagueId', '==', leagueId),
      orderBy('snapshotDate', 'desc'),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc =>
      convertTimestamps({ id: doc.id, ...doc.data() }) as LeaderboardSnapshot
    );
  },

  // Get snapshot by ID
  async getById(snapshotId: string): Promise<LeaderboardSnapshot | null> {
    const docRef = doc(db, COLLECTIONS.LEADERBOARD_SNAPSHOTS, snapshotId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return convertTimestamps({ id: docSnap.id, ...docSnap.data() }) as LeaderboardSnapshot;
    }
    return null;
  },

  // Real-time listener for latest snapshot
  subscribeToLatest(leagueId: string, callback: (snapshot: LeaderboardSnapshot | null) => void): () => void {
    const q = query(
      collection(db, COLLECTIONS.LEADERBOARD_SNAPSHOTS),
      where('leagueId', '==', leagueId),
      orderBy('snapshotDate', 'desc'),
      limit(1)
    );

    return onSnapshot(q, (querySnapshot) => {
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        callback(convertTimestamps({ id: doc.id, ...doc.data() }) as LeaderboardSnapshot);
      } else {
        callback(null);
      }
    });
  },
};

// Player Pool Update and Cascade Operations
export const playerPoolUpdateService = {
  // Update player points in a player pool
  async updatePlayerPoints(
    playerPoolId: string,
    playerUpdates: { playerId: string; points: number }[],
    updatedBy: string
  ): Promise<void> {
    try {
      console.log(`Updating ${playerUpdates.length} players in pool ${playerPoolId}`);

      // Get the player pool
      const poolDoc = await getDoc(doc(db, COLLECTIONS.PLAYER_POOLS, playerPoolId));
      if (!poolDoc.exists()) {
        throw new Error(`Player pool ${playerPoolId} not found`);
      }

      const pool = convertTimestamps({ id: poolDoc.id, ...poolDoc.data() }) as PlayerPool;

      // Update player points in the pool
      const updatedPlayers = pool.players.map(player => {
        const update = playerUpdates.find(u => u.playerId === player.playerId);
        if (update) {
          return {
            ...player,
            points: update.points,
            lastUpdated: new Date(),
            updatedBy,
          };
        }
        return player;
      });

      // Save updated pool
      await updateDoc(doc(db, COLLECTIONS.PLAYER_POOLS, playerPoolId), {
        players: updatedPlayers,
        updatedAt: new Date(),
      });

      console.log(`Player pool ${playerPoolId} updated successfully`);

      // Cascade updates to all leagues using this pool
      await this.cascadeUpdateToLeagues(playerPoolId, playerUpdates);
    } catch (error) {
      console.error('Error updating player points:', error);
      throw error;
    }
  },

  // Cascade player point updates to all leagues using this player pool
  async cascadeUpdateToLeagues(
    playerPoolId: string,
    playerUpdates: { playerId: string; points: number }[]
  ): Promise<void> {
    try {
      console.log(`Cascading updates to leagues using pool ${playerPoolId}`);

      // Find all leagues using this player pool
      const leaguesQuery = query(
        collection(db, COLLECTIONS.LEAGUES),
        where('playerPoolId', '==', playerPoolId)
      );
      const leaguesSnapshot = await getDocs(leaguesQuery);
      const leagues = leaguesSnapshot.docs.map(doc =>
        convertTimestamps({ id: doc.id, ...doc.data() }) as League
      );

      console.log(`Found ${leagues.length} leagues using this pool`);

      // Update squads and leaderboards for each league
      for (const league of leagues) {
        await this.updateLeagueSquadsAndLeaderboard(league.id, playerUpdates);
      }

      console.log('Cascade complete');
    } catch (error) {
      console.error('Error cascading updates to leagues:', error);
      throw error;
    }
  },

  // Update all squads in a league and refresh its leaderboard
  async updateLeagueSquadsAndLeaderboard(
    leagueId: string,
    playerUpdates: { playerId: string; points: number }[]
  ): Promise<void> {
    try {
      console.log(`Updating squads for league ${leagueId}`);

      // Get league to access squadSize
      const league = await leagueService.getById(leagueId);
      if (!league) {
        console.error(`League ${leagueId} not found`);
        return;
      }

      // Get all squads in the league
      const squads = await squadService.getByLeague(leagueId);
      console.log(`Found ${squads.length} squads to update`);

      if (squads.length === 0) {
        console.log(`No squads in league ${leagueId}, skipping`);
        return;
      }

      // Update each squad's player points and recalculate totals
      // IMPORTANT: Always update ALL squads on every player pool save to ensure consistency
      const batch = writeBatch(db);

      for (const squad of squads) {
        const { updatedPlayers, newTotalPoints, newCaptainPoints, newViceCaptainPoints, newXFactorPoints } =
          this.recalculateSquadPoints(squad, playerUpdates, league.squadSize);

        // Always update every squad on each player pool save
        const squadRef = doc(db, COLLECTIONS.SQUADS, squad.id);
        batch.update(squadRef, {
          players: updatedPlayers,
          totalPoints: newTotalPoints,
          captainPoints: newCaptainPoints,
          viceCaptainPoints: newViceCaptainPoints,
          xFactorPoints: newXFactorPoints,
          lastUpdated: new Date(),
        });
      }

      // Commit all squad updates
      await batch.commit();
      console.log(`Updated ${squads.length} squads in league ${leagueId}`);

      // Create a new leaderboard snapshot for this league
      console.log(`Creating new leaderboard snapshot for league ${leagueId}`);
      await leaderboardSnapshotService.create(leagueId);
      console.log(`Leaderboard snapshot created for league ${leagueId}`);
    } catch (error) {
      console.error(`Error updating league ${leagueId}:`, error);
      throw error;
    }
  },

  // Recalculate a squad's points based on player updates
  recalculateSquadPoints(
    squad: LeagueSquad,
    playerUpdates: { playerId: string; points: number }[],
    squadSize: number
  ): {
    updatedPlayers: LeagueSquad['players'];
    newTotalPoints: number;
    newCaptainPoints: number;
    newViceCaptainPoints: number;
    newXFactorPoints: number;
  } {
    // Update player points in the squad
    const updatedPlayers = squad.players.map(player => {
      const update = playerUpdates.find(u => u.playerId === player.playerId);

      if (update) {
        return {
          ...player,
          points: update.points,
        };
      }
      return player;
    });

    // FIXED: Use shared calculation utility for consistency
    // This ensures cascade updates use the SAME formula as transfers
    const calculatedPoints = calculateSquadPoints(
      updatedPlayers,
      squadSize,
      squad.captainId,
      squad.viceCaptainId,
      squad.xFactorId,
      squad.bankedPoints || 0
    );

    return {
      updatedPlayers,
      newTotalPoints: calculatedPoints.totalPoints,
      newCaptainPoints: calculatedPoints.captainPoints,
      newViceCaptainPoints: calculatedPoints.viceCaptainPoints,
      newXFactorPoints: calculatedPoints.xFactorPoints,
    };
  },
};

// Batch Operations for Performance
export const userService = {
  async getById(userId: string): Promise<User | null> {
    const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId));
    if (!userDoc.exists()) return null;
    return convertTimestamps({ uid: userDoc.id, ...userDoc.data() }) as User;
  },

  async getByIds(userIds: string[]): Promise<User[]> {
    const users = await Promise.all(
      userIds.map(async (userId) => {
        try {
          return await this.getById(userId);
        } catch (error) {
          console.error(`Error fetching user ${userId}:`, error);
          return null;
        }
      })
    );
    return users.filter((user): user is User => user !== null);
  },
};

export const batchService = {
  // Update multiple squads' points after a match
  async updateSquadPoints(squadUpdates: { squadId: string; points: number; matchId: string }[]): Promise<void> {
    const batch = writeBatch(db);

    squadUpdates.forEach(({ squadId, points, matchId }) => {
      const squadRef = doc(db, COLLECTIONS.SQUADS, squadId);
      batch.update(squadRef, {
        [`matchPoints.${matchId}`]: points,
        totalPoints: points, // This would be calculated total
      });
    });

    await batch.commit();
  },
};

// ============================================================================
// Tennis collection read services (write side is admin-SDK only, in /api).
// ============================================================================

export const tennisMatchService = {
  /**
   * Get all matches for a tournament, ordered by scheduled UTC date/time.
   */
  async getByTournament(tournamentKey: number): Promise<TennisMatch[]> {
    const q = query(
      tennisCol('matches'),
      where('tournamentKey', '==', tournamentKey),
      orderBy('dateTimeUTC', 'asc')
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => convertTimestamps({ ...d.data() }) as TennisMatch);
  },

  /**
   * Live subscription — returns the Firestore unsubscribe function.
   *
   * `onError` fires when the listen fails (e.g. missing composite index,
   * permission denied). Callers should surface the error in the UI so the
   * tab doesn't sit on a loading spinner forever.
   */
  subscribeByTournament(
    tournamentKey: number,
    callback: (matches: TennisMatch[]) => void,
    onError?: (err: Error) => void
  ): () => void {
    const q = query(
      tennisCol('matches'),
      where('tournamentKey', '==', tournamentKey),
      orderBy('dateTimeUTC', 'asc')
    );
    return onSnapshot(
      q,
      (snap) => {
        callback(snap.docs.map((d) => convertTimestamps({ ...d.data() }) as TennisMatch));
      },
      (err) => {
        if (onError) onError(err);
        else console.error('tennisMatchService.subscribeByTournament error:', err);
      }
    );
  },
};

export const tennisTournamentService = {
  /**
   * Fetch all seeded tennis tournaments — used to populate the admin dropdown.
   */
  async listAll(): Promise<TennisTournament[]> {
    const snap = await getDocs(tennisCol('tournaments'));
    return snap.docs.map((d) => convertTimestamps({ ...d.data() }) as TennisTournament);
  },

  /**
   * Fetch a single tournament by its PK (also the doc ID).
   */
  async getByKey(tournamentKey: number): Promise<TennisTournament | null> {
    const docSnap = await getDoc(tennisDoc('tournaments', String(tournamentKey)));
    if (!docSnap.exists()) return null;
    return convertTimestamps({ ...docSnap.data() }) as TennisTournament;
  },
};

// Utility Functions for Squad Player Management
export const squadPlayerUtils = {
  /**
   * Create a SquadPlayer for initial squad creation
   * Snapshots current points so only future pool gains count toward this squad's score
   */
  createInitialSquadPlayer(
    player: {
      playerId: string;
      playerName: string;
      team: string;
      role: 'batsman' | 'bowler' | 'allrounder' | 'wicketkeeper' | 'men' | 'women';
      points: number;
      price?: number;
      isOverseas?: boolean;
    }
  ): SquadPlayer {
    const squadPlayer: SquadPlayer = {
      playerId: player.playerId,
      playerName: player.playerName,
      team: player.team,
      role: player.role,
      points: player.points,
      matchPerformances: {},
      addedAt: new Date(),
      pointsAtJoining: player.points, // Snapshot at squad creation — only future gains count
    };

    // Only include price if it's defined
    if (player.price !== undefined) {
      squadPlayer.price = player.price;
    }

    // Only include isOverseas if it's defined
    if (player.isOverseas !== undefined) {
      squadPlayer.isOverseas = player.isOverseas;
    }

    return squadPlayer;
  },

  /**
   * Create a SquadPlayer for transfer (replacing another player)
   * Snapshots current points - only future points will count
   */
  createTransferSquadPlayer(
    player: {
      playerId: string;
      playerName: string;
      team: string;
      role: 'batsman' | 'bowler' | 'allrounder' | 'wicketkeeper' | 'men' | 'women';
      points: number;
      price?: number;
      isOverseas?: boolean;
    }
  ): SquadPlayer {
    const squadPlayer: SquadPlayer = {
      playerId: player.playerId,
      playerName: player.playerName,
      team: player.team,
      role: player.role,
      points: player.points,
      matchPerformances: {},
      addedAt: new Date(),
      pointsAtJoining: player.points, // Snapshot current points - only future gains count
    };

    // Only include price if it's defined
    if (player.price !== undefined) {
      squadPlayer.price = player.price;
    }

    // Only include isOverseas if it's defined
    if (player.isOverseas !== undefined) {
      squadPlayer.isOverseas = player.isOverseas;
    }

    return squadPlayer;
  },

  /**
   * Calculate effective points for a squad player
   * Returns points earned while in this squad
   */
  calculateEffectivePoints(player: SquadPlayer): number {
    const pointsAtJoining = player.pointsAtJoining ?? 0;
    return player.points - pointsAtJoining;
  },
};
